import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, FormControl, MenuItem, Select } from '@mui/material';
import { format } from 'date-fns';

import type {
    ReportConfig,
    ReportDateSettings,
    ReportProps,
    ChartReportPart,
    ReportWrapperRefAPI,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';
import useChartColors from 'c-reports/useChartColors';
import { useTheme } from '@mui/styles';

import { AutoGrid } from 'c-components';
import FieldFilter from 'c-main/Components/Campaign/CampaignReports/Views/ByField/FieldFilter';

import type {
    CampaignNoticeKey,
    ElasticSearchAvailableFilters,
    ElasticSearchFilters,
    ElasticSearchHistogramResponse,
} from 'c-sdk';
import {
    ElasticSearchHistogramInterval,
    EsPCAReportField,
    PCAReportField,
    PCAReportMetric,
} from 'c-sdk';
import apiClient from 'c-data/apiClient';
import type { ReportTabProps } from 'c-main/Components/Campaign/CampaignReports/types';
import { performanceStatsToOldCampaignStats } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';
import { useCommonTranslation } from 'c-translation';
import {
    getBaseSeriesKey,
    getComparisonSeriesKey,
    isComparisonSeriesKey,
    foldDspGroupLabel,
    pickAverageAudience,
    pickBuckets,
    pickDateKey,
    pickDeliveredImpacts,
    pickGeopathAudience,
    pickGroupKey,
    pickMetricValue,
} from './energyUsageUtils';

export type AnalyticsOverTimeSeriesMode = 'single' | 'geopath' | 'averages';
type ComparisonViewMode = 'both' | 'impacts' | 'comparison';

type Props = {
    campaignId: number;
    environment: any;
    source: any;

    metric: PCAReportMetric;
    onMetricChange?: (metric: PCAReportMetric) => void;

    field?: EsPCAReportField;
    fields?: EsPCAReportField[];

    currentStartDate: Date;
    currentEndDate: Date;
    timeFrame: ReportDateTimeframe;
    dateSettings: ReportDateSettings;

    fieldFilters?: ElasticSearchFilters;
    geopathOnly: boolean;
    audienceMode?: 'standard' | 'averages';
    comparisonLabel?: string;
    comparisonPrimary?: boolean;

    onFiltersUpdated: (filters: ElasticSearchAvailableFilters) => void;

    groupResultsByDate?: boolean;
    onResponse?: ReportTabProps['onResponse'];

    additionalControls?: React.ReactNode;
    advancedFilter?: React.ReactNode;
    showAdvancedFilters?: boolean;
    foldBadKeysToOther?: boolean;
};

const defaultFieldOptionsCount = 10;

const AnalyticsESOverTimeByField: React.FC<Props> = ({
    campaignId,
    environment,
    source,
    metric,
    onMetricChange,
    field,
    fields: explicitFields,
    currentStartDate,
    currentEndDate,
    timeFrame,
    dateSettings,
    fieldFilters,
    geopathOnly,
    audienceMode = 'standard',
    comparisonLabel,
    comparisonPrimary = false,
    onFiltersUpdated,
    groupResultsByDate = false,
    additionalControls,
    advancedFilter,
    showAdvancedFilters = false,
    onResponse,
    foldBadKeysToOther = false,
}) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const isAverageAudienceMode = audienceMode === 'averages';
    const [pcaData, setPcaData] = useState<ElasticSearchHistogramResponse>(null);
    const [comparisonViewMode, setComparisonViewMode] = useState<ComparisonViewMode>('both');
    const totalSeriesLabel = t('Modules.Main.Campaigns.Overview.Analytics.actual');
    const resolvedComparisonLabel =
        comparisonLabel ?? t('Modules.Main.Campaigns.Overview.Analytics.comparison');

    useEffect(() => {
        onFiltersUpdated((pcaData?.filters ?? {}) as any);
    }, [pcaData?.filters, onFiltersUpdated]);

    const isMultiField = Array.isArray(explicitFields) && explicitFields.length > 1;
    const fetchData: ReportConfig<any>['fetchData'] = useCallback(
        (start, end, timeframe) => {
            const isOneDay = timeframe === ReportDateTimeframe.Daily;
            const fields = isMultiField
                ? (explicitFields as EsPCAReportField[])
                : field === EsPCAReportField.DisplayOwnerFormat
                ? [EsPCAReportField.DisplayOwner, EsPCAReportField.DisplayFormat]
                : [field ?? EsPCAReportField.StartDate];
            return apiClient.ReportsElasticSearch.histogram({
                campaignId,
                showStats: true,
                interval: isOneDay
                    ? ElasticSearchHistogramInterval.Hour
                    : ElasticSearchHistogramInterval.Day,
                startDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(start, 'yyyy-MM-dd')} 00:00:00`,
                endDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(end, 'yyyy-MM-dd')} 23:59:59`,
                fields,
                environment,
                source,
                fieldFilters,
                dataStatus: fieldFilters?.[PCAReportField.DataStatus] as CampaignNoticeKey[],
            });
        },
        [campaignId, environment, field, explicitFields, isMultiField, fieldFilters, source],
    );

    const onDataUpdated = useCallback(
        (data, start, end, timeframe) => {
            setPcaData(data);

            if (data?.campaign_stats && onResponse) {
                onResponse(
                    {
                        campaignStats: performanceStatsToOldCampaignStats(data.campaign_stats),
                        filters: (data?.filters ?? {}) as any,
                    },
                    start,
                    end,
                    timeframe,
                );
            }
        },
        [onResponse],
    );

    const rawChartData = useMemo(() => {
        const buckets = pickBuckets(pcaData, field, explicitFields);

        const byX = buckets.reduce<Record<string, Record<string, number>>>((acc, b) => {
            const x = pickDateKey(b);
            if (!x) return acc;

            const rawGroup =
                groupResultsByDate || (!field && !isMultiField)
                    ? undefined
                    : isMultiField
                    ? b?.group ?? ''
                    : pickGroupKey(b, field);
            const group =
                rawGroup !== undefined && foldBadKeysToOther
                    ? foldDspGroupLabel(rawGroup)
                    : rawGroup;

            if (!acc[x]) acc[x] = {};

            if (geopathOnly || isAverageAudienceMode) {
                const delivered = pickDeliveredImpacts(b);
                const comparison = geopathOnly ? pickGeopathAudience(b) : pickAverageAudience(b);

                // The base key holds the primary series (solid line) and its comparison
                // key the secondary (dashed). When comparisonPrimary, the audience /
                // average figure is primary and delivered impressions are secondary.
                const baseKey = group
                    ? String(group)
                    : comparisonPrimary
                    ? resolvedComparisonLabel
                    : totalSeriesLabel;
                const comparisonKey = getComparisonSeriesKey(baseKey);
                acc[x][baseKey] =
                    (acc[x][baseKey] ?? 0) + (comparisonPrimary ? comparison : delivered);
                acc[x][comparisonKey] =
                    (acc[x][comparisonKey] ?? 0) + (comparisonPrimary ? delivered : comparison);
            } else {
                const v = pickMetricValue(b, metric);

                if (!group) {
                    acc[x].Actual = (acc[x].Actual ?? 0) + v;
                } else {
                    const g = String(group);
                    acc[x][g] = (acc[x][g] ?? 0) + v;
                }
            }

            return acc;
        }, {});

        return Object.entries(byX)
            .sort(([a], [b]) => (a > b ? 1 : -1))
            .map(([x, series]) => ({ x, ...series }));
    }, [
        pcaData,
        field,
        explicitFields,
        isMultiField,
        geopathOnly,
        isAverageAudienceMode,
        groupResultsByDate,
        metric,
        resolvedComparisonLabel,
        comparisonPrimary,
        totalSeriesLabel,
        foldBadKeysToOther,
    ]);

    const mode: AnalyticsOverTimeSeriesMode = geopathOnly
        ? 'geopath'
        : isAverageAudienceMode
        ? 'averages'
        : 'single';

    const allBaseSeries = useMemo(() => {
        if (!rawChartData?.length) return [];

        const keys = rawChartData.flatMap(row => Object.keys(row)).filter(k => k !== 'x');

        const unique = Array.from(new Set(keys));

        if (mode === 'geopath' || mode === 'averages') {
            return unique.filter(k => !isComparisonSeriesKey(k));
        }

        return unique;
    }, [rawChartData, mode]);

    const [selectedFields, setSelectedFields] = useState<string[] | undefined>(undefined);

    useEffect(() => {
        setSelectedFields(undefined);
    }, [field, geopathOnly, isAverageAudienceMode, groupResultsByDate]);

    useEffect(() => {
        setComparisonViewMode('both');
    }, [field, geopathOnly, isAverageAudienceMode, groupResultsByDate, comparisonLabel]);

    const actualSelectedBaseSeries = useMemo(() => {
        const updatedAll = allBaseSeries;

        if (groupResultsByDate || !field) return updatedAll;

        const withoutMissing = (selectedFields ?? []).filter(sel => updatedAll.includes(sel));
        const allExcludingSelected = updatedAll.filter(sel => !withoutMissing.includes(sel));

        if (withoutMissing.length === 0 && selectedFields == null) {
            return allExcludingSelected.slice(0, defaultFieldOptionsCount);
        }

        return withoutMissing;
    }, [allBaseSeries, field, groupResultsByDate, selectedFields]);

    const chartData = useMemo(() => {
        if (!rawChartData?.length) return rawChartData;

        const allowed = new Set<string>(
            actualSelectedBaseSeries.flatMap(base =>
                mode === 'geopath' || mode === 'averages'
                    ? [base, getComparisonSeriesKey(base)]
                    : [base],
            ),
        );

        return rawChartData.map(row =>
            Object.keys(row).reduce<Record<string, any>>(
                (acc, key) => {
                    if (key === 'x') {
                        acc.x = row.x;
                        return acc;
                    }

                    if (!allowed.has(key)) return acc;

                    if (mode === 'geopath' || mode === 'averages') {
                        const isComparisonKey = isComparisonSeriesKey(key);
                        const holdsImpacts = comparisonPrimary ? isComparisonKey : !isComparisonKey;

                        if (comparisonViewMode === 'impacts' && !holdsImpacts) {
                            return acc;
                        }

                        if (comparisonViewMode === 'comparison' && holdsImpacts) {
                            return acc;
                        }
                    }

                    acc[key] = row[key];
                    return acc;
                },
                { x: row.x },
            ),
        );
    }, [rawChartData, actualSelectedBaseSeries, comparisonViewMode, comparisonPrimary, mode]);

    const chartColors = useChartColors();

    const parts = useMemo<ChartReportPart[]>(() => {
        if (!chartData?.length) return [];

        if (mode === 'geopath' || mode === 'averages') {
            return actualSelectedBaseSeries.flatMap((base, idx) => {
                const color = chartColors[idx % chartColors.length];
                const output: ChartReportPart[] = [];
                const isGrouped = base !== resolvedComparisonLabel && base !== totalSeriesLabel;
                const withGroup = (label: string) =>
                    isGrouped ? `${getBaseSeriesKey(base)} ${label}` : label;
                const showImpacts = comparisonViewMode !== 'comparison';
                const showComparison = comparisonViewMode !== 'impacts';

                if (comparisonPrimary ? showComparison : showImpacts) {
                    output.push({
                        dataKey: base,
                        labelOverride: comparisonPrimary
                            ? withGroup(resolvedComparisonLabel)
                            : undefined,
                        lineProps: { stroke: color },
                    });
                }

                if (comparisonPrimary ? showImpacts : showComparison) {
                    output.push({
                        dataKey: getComparisonSeriesKey(base),
                        labelOverride: comparisonPrimary
                            ? withGroup(totalSeriesLabel)
                            : withGroup(resolvedComparisonLabel),
                        lineProps: { stroke: color, strokeDasharray: '5 5' },
                    });
                }

                return output;
            });
        }

        return actualSelectedBaseSeries.map((base, idx) => ({
            dataKey: base,
            lineProps: { stroke: chartColors[idx % chartColors.length] },
        }));
    }, [
        actualSelectedBaseSeries,
        chartColors,
        chartData,
        comparisonViewMode,
        comparisonPrimary,
        mode,
        resolvedComparisonLabel,
        totalSeriesLabel,
    ]);

    const reportProps = useMemo<ReportProps<unknown>[]>(
        () => [
            {
                type: ReportType.Line,
                namePrefix: `${campaignId}_analytics_overtime`,
                xAxisDataKey: 'x',
                data: chartData,
                parts,
                yAxisTickFormatterAsTime:
                    !(geopathOnly || isAverageAudienceMode) && metric === PCAReportMetric.Time,
            },
        ],
        [campaignId, chartData, geopathOnly, isAverageAudienceMode, metric, parts],
    );

    const beforeChartComponent = useMemo(
        () => (
            <>
                {advancedFilter && (
                    <Collapse in={showAdvancedFilters}>
                        <Box>{advancedFilter}</Box>
                    </Collapse>
                )}
                {!groupResultsByDate && field && (
                    <Box>
                        <AutoGrid spacing={2} xs={12} sm={6} md="auto" alignItems="center">
                            <FieldFilter
                                allFields={allBaseSeries}
                                selectedFieldValues={actualSelectedBaseSeries}
                                setSelectedFieldValues={setSelectedFields}
                            />

                            <Box sx={{ mb: 1 }}>
                                <FormControl size="small">
                                    <Select
                                        value={
                                            geopathOnly || isAverageAudienceMode
                                                ? comparisonViewMode
                                                : metric
                                        }
                                        onChange={e => {
                                            if (geopathOnly || isAverageAudienceMode) {
                                                setComparisonViewMode(
                                                    e.target.value as ComparisonViewMode,
                                                );
                                                return;
                                            }

                                            onMetricChange?.(e.target.value as PCAReportMetric);
                                        }}
                                        sx={{ backgroundColor: 'background.paper' }}
                                    >
                                        {geopathOnly || isAverageAudienceMode
                                            ? [
                                                  <MenuItem key="impacts" value="impacts">
                                                      {t(
                                                          'Modules.Main.Campaigns.Overview.Analytics.impactsTitle',
                                                      )}
                                                  </MenuItem>,
                                                  <MenuItem key="comparison" value="comparison">
                                                      {resolvedComparisonLabel}
                                                  </MenuItem>,
                                                  <MenuItem key="both" value="both">
                                                      {t(
                                                          'Modules.Main.Campaigns.Overview.Analytics.both',
                                                      )}
                                                  </MenuItem>,
                                              ]
                                            : [
                                                  <MenuItem
                                                      key={PCAReportMetric.Impacts}
                                                      value={PCAReportMetric.Impacts}
                                                  >
                                                      {t(
                                                          'Modules.Main.Campaigns.Overview.Analytics.impressions',
                                                      )}
                                                  </MenuItem>,
                                                  <MenuItem
                                                      key={PCAReportMetric.Plays}
                                                      value={PCAReportMetric.Plays}
                                                  >
                                                      {t(
                                                          'Modules.Main.Campaigns.Overview.Analytics.plays',
                                                      )}
                                                  </MenuItem>,
                                                  <MenuItem
                                                      key={PCAReportMetric.Time}
                                                      value={PCAReportMetric.Time}
                                                  >
                                                      {t(
                                                          'Modules.Main.Campaigns.Overview.Analytics.timePlayed',
                                                      )}
                                                  </MenuItem>,
                                              ]}
                                    </Select>
                                </FormControl>
                            </Box>
                        </AutoGrid>
                    </Box>
                )}
            </>
        ),
        [
            advancedFilter,
            showAdvancedFilters,
            geopathOnly,
            isAverageAudienceMode,
            metric,
            onMetricChange,
            groupResultsByDate,
            field,
            allBaseSeries,
            actualSelectedBaseSeries,
            comparisonViewMode,
            resolvedComparisonLabel,
            t,
        ],
    );
    const reportRef = useRef<ReportWrapperRefAPI>(null);

    const fieldsKey = useMemo(() => (explicitFields ?? []).join(','), [explicitFields]);
    useEffect(() => {
        reportRef.current?.refreshData?.();
    }, [fieldFilters, geopathOnly, isAverageAudienceMode, metric, field, fieldsKey]);

    return (
        <ReportWrapper
            requestKeyPrefix=""
            ref={reportRef}
            initialStartDate={currentStartDate}
            initialEndDate={currentEndDate}
            dateSettings={dateSettings}
            fetchData={fetchData}
            onDataUpdated={onDataUpdated}
            reportProps={reportProps as any}
            beforeChartComponent={beforeChartComponent}
            additionalDateControls={<>{additionalControls}</>}
            additionalDateControlsPosition="end"
            reportChartWrapperProps={{ height: '75vh' }}
            reportWrapperProps={{
                sx: {
                    bgcolor: 'background.paper',
                    mt: 1,
                    pb: 1,
                    pt: 1,
                },
            }}
        />
    );
};

export default AnalyticsESOverTimeByField;
